// The job form's Apollo URL rule - the same as the server's
// (backend/utils/apollo_url.py), so the form catches what the server would
// refuse instead of sending it and showing a validation error.
// apollo_url_parity_test.py runs both on the same URLs.

const MAX_LENGTH = 32000;
// A longer paste is left untidied and refused as too long (as on the server).
const MAX_PASTE_LENGTH = 2 * MAX_LENGTH;

// Character sets spelled out explicitly (not \s), identical to the server's.
const SPACES = ' \u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u202f\u205f\u3000';
const LINE_BREAKS = '\r\n\u000b\u000c\u0085\u2028\u2029';
const TRIM = SPACES + LINE_BREAKS + '\t\ufeff';
const INDENT = ' \t\u00a0';

const escapeClass = (chars) => chars.replace(/[\\\]^-]/g, '\\$&');
const LINE_BREAK_RUN = new RegExp(`[${escapeClass(LINE_BREAKS)}]+`);
const SPACE_CHARS = new RegExp(`[${escapeClass(SPACES)}]`, 'g');

// Strip `chars` from the ends of `text` by hand: a regex such as /\s+$/
// rescans a long inner run of spaces from every position in it, which takes
// quadratic time and freezes the tab on a large paste.
function stripChars(text, chars, { start = true, end = true } = {}) {
  let from = 0;
  let to = text.length;
  if (start) while (from < to && chars.includes(text[from])) from += 1;
  if (end) while (to > from && chars.includes(text[to - 1])) to -= 1;
  return text.slice(from, to);
}

// Remove each line break together with the indentation around it - what
// wrapping in chat or email leaves behind in a long URL.
function removeWrapping(text) {
  const pieces = text.split(LINE_BREAK_RUN);
  if (pieces.length === 1) return text;
  const last = pieces.length - 1;
  return pieces
    .map((piece, i) => stripChars(piece, INDENT, { start: i > 0, end: i < last }))
    .join('');
}

// Tidy a pasted URL the way a browser would have copied it, without changing
// what it points to: trim, drop wrapping artifacts (line breaks with their
// indentation, tabs, byte-order marks), encode any other space as %20 rather
// than deleting it, and give a bare apollo.io link https://.
export function normalizeApolloUrl(url) {
  if (typeof url !== 'string' || url.length > MAX_PASTE_LENGTH) return url;
  let tidy = removeWrapping(stripChars(url, TRIM));
  tidy = tidy.replace(/\t/g, '').replace(/\ufeff/g, '');
  tidy = tidy.replace(SPACE_CHARS, '%20');
  const lowered = tidy.toLowerCase();
  if (!tidy.includes('://') &&
      (lowered.startsWith('apollo.io') || lowered.startsWith('app.apollo.io') || lowered.startsWith('www.apollo.io'))) {
    return `https://${tidy}`;
  }
  return tidy;
}

// eslint-disable-next-line no-control-regex
const FORBIDDEN = /[\\<>"`\u0000-\u001f\u007f]/;

// null if `url` (already normalized) is an Apollo People-search URL,
// otherwise the reason it is not. Never throws: it runs on every render of
// the job form, and an exception there blanks the whole page.
export function apolloUrlProblem(url) {
  try {
    if (typeof url !== 'string' || !url) return 'Apollo URL is required';
    if (url.length > MAX_LENGTH) return 'Apollo URL is too long';
    if (FORBIDDEN.test(url)) return 'Apollo URL contains characters that are not allowed';
    // Require "scheme://host" literally - browsers also accept "https:host",
    // which the server does not.
    if (!/^https?:\/\/[^/]/i.test(url)) return 'URL must be an https:// link from Apollo.io';

    const parsed = new URL(url);
    const authority = url.slice(url.indexOf('://') + 3).split(/[/?#]/)[0];
    if (!authority || authority.includes('@') || parsed.username || parsed.password) {
      return 'URL must be a link to Apollo.io';
    }

    const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
    if (host.includes('%') || (host !== 'apollo.io' && !host.endsWith('.apollo.io'))) {
      return 'URL must be from Apollo.io';
    }
    const fragment = parsed.hash.replace(/^#/, '');
    if (!fragment.startsWith('/people') && !parsed.pathname.startsWith('/people')) {
      return 'URL must be from Apollo.io People search and contain "/people?"';
    }
    return null;
  } catch (e) {
    return 'Apollo URL is not a valid URL';
  }
}
